/* ADR-012's local auth: the bearer token this browser holds, client-side only.
 *
 * `sessionStorage` rather than `localStorage`, deliberately — a token that outlives the tab is a
 * token that outlives the person who logged in on a shared machine. It is lost on tab close, which
 * is the correct failure: log in again, the same as any other session-scoped credential.
 *
 * No cookie, no `httpOnly` storage: this frontend talks to `lumiq-core` directly (or through
 * `api-gateway`), never through a same-origin proxy that could set one, so the token has to be
 * readable here to be attached as `Authorization: Bearer`. That is the ordinary shape for an API
 * this platform's own edge gateway fronts — see `shared/api/http.ts` for where it is attached.
 */

import { isEmbedded } from '@/shared/embedded';

const STORAGE_KEY = 'lumiq.session.token';
/* Kept beside the token rather than decoded out of it. The obligation *is* a claim inside the JWT,
 * and `LoginResult` exists on the wire precisely so a browser does not have to decode a token it is
 * not supposed to interpret — see the port's own docstring. Storing the answer the server gave is
 * the honest shape; parsing the credential to re-derive it would be the shortcut. */
const MUST_CHANGE_KEY = 'lumiq.session.mustChangePassword';

let token: string | null = readStoredToken();
let obligation: boolean = readStoredFlag();

function readStoredToken(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    /* Storage can throw in a locked-down browsing context (private mode with storage disabled).
     * An unreadable store means "not logged in" rather than a crash on module load. */
    return null;
  }
}

function readStoredFlag(): boolean {
  try {
    return sessionStorage.getItem(MUST_CHANGE_KEY) === '1';
  } catch {
    /* See readStoredToken. An unreadable store means "no obligation recorded" — and that is the
     * safe direction *here* rather than the unsafe one, because the server refuses every route
     * anyway: the flag only decides where this browser sends the reader, never whether the
     * platform lets them in. */
    return false;
  }
}

export function currentToken(): string | null {
  return token;
}

/** Whether this session must set a new password before it can be used for anything else.
 *
 * **Only decides navigation.** `OidcTenantResolver` refuses every other authenticated route while
 * the token carries the obligation, so this flag cannot grant access — getting it wrong sends a
 * reader to the wrong screen and never past the gate. That asymmetry is why reading it from
 * storage can fail closed to `false` without opening anything. */
export function mustChangePassword(): boolean {
  return obligation;
}

export function isAuthenticated(): boolean {
  return token !== null;
}

/** The token the Suite shell holds, pushed in by the remote's `mount()` and on every refresh.
 *
 * **Memory only, never `sessionStorage`.** Inside the shell the session belongs to the shell — it signs
 * in, refreshes and signs out — so a copy written here would be a second credential store on the
 * suite's origin that outlives the shell's own sign-out. `null` on unmount, so nothing survives it.
 *
 * No password-change obligation: the shell refuses to sign in an account that carries one.
 */
export function setExternalToken(next: string | null): void {
  token = next;
  obligation = false;
}

export function setToken(next: string, pending = false): void {
  token = next;
  obligation = pending;
  /* Embedded, the in-memory copy is the whole of it; see `setExternalToken`. */
  if (isEmbedded()) return;
  try {
    if (pending) sessionStorage.setItem(MUST_CHANGE_KEY, '1');
    else sessionStorage.removeItem(MUST_CHANGE_KEY);
  } catch {
    /* See readStoredToken. */
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* Kept in memory even if storage refuses the write — the session still works for this tab
     * and simply does not survive a reload, which is a smaller failure than losing the login. */
  }
}

/** Clears the held token. Called on an explicit logout, and on a 401 from the API — an expired or
 * revoked token is functionally the same as no token, and the app should ask to log in again
 * rather than keep sending a credential the server has already rejected. */
export function clearToken(): void {
  token = null;
  obligation = false;
  if (isEmbedded()) return;
  try {
    sessionStorage.removeItem(MUST_CHANGE_KEY);
  } catch {
    /* See readStoredToken. */
  }
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* See readStoredToken. */
  }
}
